import { HighlightIcon } from '@dev-spendesk/grapes';
import { useMemo, useState } from 'react';

import { QuerySuspense } from 'src/core/common/components/QuerySuspense';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { getDimensionIcon } from 'src/core/modules/budgets/budgets-by-dimensions/models/dimension';

import { useBreakdownQuery } from '../../../../containers/hooks/useBreakdownQuery';
import { useExpenseCategoriesBreakdownQuery } from '../../../../hooks/useExpenseCategoriesBreakdownQuery';
import { isBudgetBreakdownEmpty } from '../../../../models/breakdown';
import { type Budget } from '../../../../models/budget';
import { type BudgetaryExercise } from '../../../../models/budgetaryExercise';
import {
  type ExpenseCategoryBreakdown,
  getExpenseCategoryName,
} from '../../../../models/expenseCategoryBreakdown';
import {
  type PeriodRange,
  toPeriodRangePayload,
} from '../../../../models/periodRange';
import { BudgetRow } from '../../v1/BudgetCard/BudgetRow';

export type BudgetCardRowV2Props = {
  budget: Budget;
  periodRange: PeriodRange;
  budgetaryExercise: BudgetaryExercise;
  depth?: number;
  onRowClick: (budgetId: string, expenseCategoryId?: string) => void;
};

export const BudgetCardRowV2 = ({
  budget,
  periodRange,
  budgetaryExercise,
  depth = 0,
  onRowClick,
}: BudgetCardRowV2Props) => {
  const { t } = useTranslation('global');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const periodRangePayload = useMemo(
    () =>
      toPeriodRangePayload(
        budgetaryExercise.periodicity === 'yearly'
          ? { key: 'overall' }
          : periodRange,
      ),
    [budgetaryExercise.periodicity, periodRange],
  );

  const breakdownQueryState = useBreakdownQuery(budget.id, periodRangePayload);

  const expenseCategoriesQueryState = useExpenseCategoriesBreakdownQuery(
    budget.id,
    periodRangePayload,
  );

  if (breakdownQueryState.status === 'loading') {
    return null;
  }

  if (breakdownQueryState.status === 'error') {
    return null;
  }

  const getCollapse = () => {
    const hasExpenseCategories =
      expenseCategoriesQueryState.status === 'success' &&
      expenseCategoriesQueryState.data.expenseCategoriesBreakdown.some(
        shouldDisplayBreakdown,
      );
    if (hasExpenseCategories) {
      return {
        isCollapsed,
        onToogleCollapse: () => setIsCollapsed(!isCollapsed),
      };
    }
  };

  return (
    <>
      <BudgetRow
        rowName={budget.name}
        breakdown={breakdownQueryState.data}
        collapse={getCollapse()}
        onRowClick={() => onRowClick(budget.id)}
        depth={depth}
        className="py-8"
        rowIcon={
          <HighlightIcon
            size={32}
            {...getDimensionIcon({ type: 'costCenter' })}
          />
        }
      />

      {!isCollapsed && (
        <QuerySuspense
          queryState={expenseCategoriesQueryState}
          loading={null}
          fallback={() => null}
        >
          {(expenseCategoriesData) => (
            <>
              {expenseCategoriesData.expenseCategoriesBreakdown
                .filter(shouldDisplayBreakdown)
                .map((ecBreakdown) => (
                  <BudgetRow
                    key={ecBreakdown.expenseCategory.id}
                    rowName={getExpenseCategoryName(
                      ecBreakdown.expenseCategory,
                      t,
                    )}
                    breakdown={ecBreakdown}
                    isArchived={
                      !ecBreakdown.expenseCategory.isOther &&
                      ecBreakdown.expenseCategory.isArchived
                    }
                    onRowClick={() =>
                      onRowClick(budget.id, ecBreakdown.expenseCategory.id)
                    }
                    depth={depth + 1}
                    className="py-8"
                    rowIcon={
                      <HighlightIcon
                        size={32}
                        {...getDimensionIcon({ type: 'expenseCategory' })}
                      />
                    }
                  />
                ))}
            </>
          )}
        </QuerySuspense>
      )}
    </>
  );
};

const shouldDisplayBreakdown = (breakdown: ExpenseCategoryBreakdown): boolean =>
  !isBudgetBreakdownEmpty(breakdown) &&
  breakdown.expenseCategory.id !== 'default';
